/* eslint-disable */

export interface GetEmailTypeRequest {
  username: string;
}

export interface GetEmailTypeResponse {
  emailType: EmailType;
}

/**
 *  Represents a request to send a verification email to the specified email address.
 *  The corresponding response is GetCodeResponse.
 */
export interface GetCodeRequest {
  /**
   *  The email address to send the verification code to.
   */
  email: string;
  /**
   *  The targeted reporting form.
   */
  reportingForm: ReportingForm;
}

/**
 *  Represents a response after a sent verification email.
 */
export interface GetCodeResponse {
  /**
   *  The detected type of the email.
   */
  emailType: EmailType;
  /**
   *  The UUID of the verification resource. Needs to be sent in ValidateCodeRequest.
   */
  resourceUri: string;
}

/**
 *  Represents a request to validate a code.
 *  The corresponding response does not have a payload and uses HTTP status codes only.
 */
export interface ValidateCodeRequest {
  /**
   *  The email address that was used to request the code.
   */
  email: string;
  /**
   *  The UUID of the verification resource, as specified in the preceding GetCodeResponse.
   */
  resourceUri: string;
  /**
   *  The verification code to be validated.
   */
  code: string;
}

const baseGetEmailTypeRequest: object = {
  username: '',
};

const baseGetEmailTypeResponse: object = {
  emailType: 0,
};

const baseGetCodeRequest: object = {
  email: '',
  reportingForm: 0,
};

const baseGetCodeResponse: object = {
  emailType: 0,
  resourceUri: '',
};

const baseValidateCodeRequest: object = {
  email: '',
  resourceUri: '',
  code: '',
};

export const protobufPackage = 'spotify.tintin.reporting.v1';

export enum ReportingForm {
  REPORTING_FORM_UNSPECIFIED = 0,
  REPORTING_FORM_LEGAL = 1,
  REPORTING_FORM_POLICY = 2,
}

export function reportingFormFromJSON(object: any): ReportingForm {
  switch (object) {
    case 0:
    case 'REPORTING_FORM_UNSPECIFIED':
      return ReportingForm.REPORTING_FORM_UNSPECIFIED;
    case 1:
    case 'REPORTING_FORM_LEGAL':
      return ReportingForm.REPORTING_FORM_LEGAL;
    case 2:
    case 'REPORTING_FORM_POLICY':
      return ReportingForm.REPORTING_FORM_POLICY;
    default:
      throw new globalThis.Error('Unrecognized enum value ' + object + ' for enum ReportingForm');
  }
}

export function reportingFormToJSON(object: ReportingForm): string {
  switch (object) {
    case ReportingForm.REPORTING_FORM_UNSPECIFIED:
      return 'REPORTING_FORM_UNSPECIFIED';
    case ReportingForm.REPORTING_FORM_LEGAL:
      return 'REPORTING_FORM_LEGAL';
    case ReportingForm.REPORTING_FORM_POLICY:
      return 'REPORTING_FORM_POLICY';
    default:
      return 'UNKNOWN';
  }
}

export enum EmailType {
  EMAIL_TYPE_INVALID = 0,
  EMAIL_TYPE_SPOTIFY = 1,
  EMAIL_TYPE_EXTERNAL = 2,
}

export function emailTypeFromJSON(object: any): EmailType {
  switch (object) {
    case 0:
    case 'EMAIL_TYPE_INVALID':
      return EmailType.EMAIL_TYPE_INVALID;
    case 1:
    case 'EMAIL_TYPE_SPOTIFY':
      return EmailType.EMAIL_TYPE_SPOTIFY;
    case 2:
    case 'EMAIL_TYPE_EXTERNAL':
      return EmailType.EMAIL_TYPE_EXTERNAL;
    default:
      throw new globalThis.Error('Unrecognized enum value ' + object + ' for enum EmailType');
  }
}

export function emailTypeToJSON(object: EmailType): string {
  switch (object) {
    case EmailType.EMAIL_TYPE_INVALID:
      return 'EMAIL_TYPE_INVALID';
    case EmailType.EMAIL_TYPE_SPOTIFY:
      return 'EMAIL_TYPE_SPOTIFY';
    case EmailType.EMAIL_TYPE_EXTERNAL:
      return 'EMAIL_TYPE_EXTERNAL';
    default:
      return 'UNKNOWN';
  }
}

export const GetEmailTypeRequest = {
  fromJSON(object: any): GetEmailTypeRequest {
    const message = { ...baseGetEmailTypeRequest } as GetEmailTypeRequest;
    if (object.username !== undefined && object.username !== null) {
      message.username = String(object.username);
    } else {
      message.username = '';
    }
    return message;
  },
  fromPartial(object: DeepPartial<GetEmailTypeRequest>): GetEmailTypeRequest {
    const message = { ...baseGetEmailTypeRequest } as GetEmailTypeRequest;
    if (object.username !== undefined && object.username !== null) {
      message.username = object.username;
    } else {
      message.username = '';
    }
    return message;
  },
  toJSON(message: GetEmailTypeRequest): unknown {
    const obj: any = {};
    message.username !== undefined && (obj.username = message.username);
    return obj;
  },
};

export const GetEmailTypeResponse = {
  fromJSON(object: any): GetEmailTypeResponse {
    const message = { ...baseGetEmailTypeResponse } as GetEmailTypeResponse;
    if (object.emailType !== undefined && object.emailType !== null) {
      message.emailType = emailTypeFromJSON(object.emailType);
    } else {
      message.emailType = 0;
    }
    return message;
  },
  fromPartial(object: DeepPartial<GetEmailTypeResponse>): GetEmailTypeResponse {
    const message = { ...baseGetEmailTypeResponse } as GetEmailTypeResponse;
    if (object.emailType !== undefined && object.emailType !== null) {
      message.emailType = object.emailType;
    } else {
      message.emailType = 0;
    }
    return message;
  },
  toJSON(message: GetEmailTypeResponse): unknown {
    const obj: any = {};
    message.emailType !== undefined && (obj.emailType = emailTypeToJSON(message.emailType));
    return obj;
  },
};

export const GetCodeRequest = {
  fromJSON(object: any): GetCodeRequest {
    const message = { ...baseGetCodeRequest } as GetCodeRequest;
    if (object.email !== undefined && object.email !== null) {
      message.email = String(object.email);
    } else {
      message.email = '';
    }
    if (object.reportingForm !== undefined && object.reportingForm !== null) {
      message.reportingForm = reportingFormFromJSON(object.reportingForm);
    } else {
      message.reportingForm = 0;
    }
    return message;
  },
  fromPartial(object: DeepPartial<GetCodeRequest>): GetCodeRequest {
    const message = { ...baseGetCodeRequest } as GetCodeRequest;
    if (object.email !== undefined && object.email !== null) {
      message.email = object.email;
    } else {
      message.email = '';
    }
    if (object.reportingForm !== undefined && object.reportingForm !== null) {
      message.reportingForm = object.reportingForm;
    } else {
      message.reportingForm = 0;
    }
    return message;
  },
  toJSON(message: GetCodeRequest): unknown {
    const obj: any = {};
    message.email !== undefined && (obj.email = message.email);
    message.reportingForm !== undefined && (obj.reportingForm = reportingFormToJSON(message.reportingForm));
    return obj;
  },
};

export const GetCodeResponse = {
  fromJSON(object: any): GetCodeResponse {
    const message = { ...baseGetCodeResponse } as GetCodeResponse;
    if (object.emailType !== undefined && object.emailType !== null) {
      message.emailType = emailTypeFromJSON(object.emailType);
    } else {
      message.emailType = 0;
    }
    if (object.resourceUri !== undefined && object.resourceUri !== null) {
      message.resourceUri = String(object.resourceUri);
    } else {
      message.resourceUri = '';
    }
    return message;
  },
  fromPartial(object: DeepPartial<GetCodeResponse>): GetCodeResponse {
    const message = { ...baseGetCodeResponse } as GetCodeResponse;
    if (object.emailType !== undefined && object.emailType !== null) {
      message.emailType = object.emailType;
    } else {
      message.emailType = 0;
    }
    if (object.resourceUri !== undefined && object.resourceUri !== null) {
      message.resourceUri = object.resourceUri;
    } else {
      message.resourceUri = '';
    }
    return message;
  },
  toJSON(message: GetCodeResponse): unknown {
    const obj: any = {};
    message.emailType !== undefined && (obj.emailType = emailTypeToJSON(message.emailType));
    message.resourceUri !== undefined && (obj.resourceUri = message.resourceUri);
    return obj;
  },
};

export const ValidateCodeRequest = {
  fromJSON(object: any): ValidateCodeRequest {
    const message = { ...baseValidateCodeRequest } as ValidateCodeRequest;
    if (object.email !== undefined && object.email !== null) {
      message.email = String(object.email);
    } else {
      message.email = '';
    }
    if (object.resourceUri !== undefined && object.resourceUri !== null) {
      message.resourceUri = String(object.resourceUri);
    } else {
      message.resourceUri = '';
    }
    if (object.code !== undefined && object.code !== null) {
      message.code = String(object.code);
    } else {
      message.code = '';
    }
    return message;
  },
  fromPartial(object: DeepPartial<ValidateCodeRequest>): ValidateCodeRequest {
    const message = { ...baseValidateCodeRequest } as ValidateCodeRequest;
    if (object.email !== undefined && object.email !== null) {
      message.email = object.email;
    } else {
      message.email = '';
    }
    if (object.resourceUri !== undefined && object.resourceUri !== null) {
      message.resourceUri = object.resourceUri;
    } else {
      message.resourceUri = '';
    }
    if (object.code !== undefined && object.code !== null) {
      message.code = object.code;
    } else {
      message.code = '';
    }
    return message;
  },
  toJSON(message: ValidateCodeRequest): unknown {
    const obj: any = {};
    message.email !== undefined && (obj.email = message.email);
    message.resourceUri !== undefined && (obj.resourceUri = message.resourceUri);
    message.code !== undefined && (obj.code = message.code);
    return obj;
  },
};

type Builtin = Date | Function | Uint8Array | string | number | undefined;
type DeepPartial<T> = T extends Builtin
  ? T
  : T extends Array<infer U>
  ? Array<DeepPartial<U>>
  : T extends ReadonlyArray<infer U>
  ? ReadonlyArray<DeepPartial<U>>
  : T extends {}
  ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;
